import { useState } from 'react'
import { Betrag } from './Betrag'
import { api, betrag, periodenname } from './api'
import {
  aktuelleSprache,
  anzahlwort,
  gesellschaftsname,
  herleitungsname,
  istAnzahlfeld,
  istProzentfeld,
  kontobezeichnung,
  prozentangabe,
  regelname,
  satz,
  t,
} from './sprache'
import { ohneWert } from './Ohnewert'
import type { Buchungszeile, Textwert, Vorgangskopf } from './typen'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'

/**
 * Der Name eines Konsolidierungsschritts kommt aus den Stammdaten, nicht aus
 * einer Sprachdatei — die API liefert ihn zweisprachig mit.
 *
 * Das ist die zweite Ebene der Mehrsprachigkeit aus ADR 010: Oberflächentexte
 * liegen in Sprachdateien, **fachliche Bezeichnungen mehrsprachig in den
 * Stammdaten**. Kontenbezeichnungen und Berichtszeilen gehen denselben Weg.
 */
export function vorgangsname(vorgang: Vorgangskopf): string {
  return aktuelleSprache() === 'de' ? vorgang.nameDe : vorgang.nameEn || vorgang.nameDe
}

/**
 * Ein Wert der Herleitung, gesetzt nach seiner Art.
 *
 * Der Kern liefert kanonisch — „40000.00", unabhängig von jeder Sprache. Erst
 * hier wird daraus die Schreibweise, die der Anwender gewählt hat. Ohne das
 * stünde in der Herleitung eine andere Zahl als zwei Zentimeter weiter in der
 * Spalte, und der Anwender fragt sich, ob es dieselbe ist.
 */
export function schrittwert(
  schritt: Textwert,

  /** Der Namensgeber für Konten — die Prüferansicht bringt ihren eigenen mit. */
  kontoname: (schluessel: string) => string = kontobezeichnung,
): string {
  // **Ein Anteil ist kein Betrag** (R840): Der Rechenkern liefert ihn in
  // Prozent — 80 —, und so steht er im Prüfcode. Über `betrag` stand in der
  // Herleitung „80,00" ohne Zeichen; jetzt steht dort „80 %".
  // **Eine Anzahl ist kein Betrag** (R841): „120 Monate", nicht „120,00".
  if ((schritt.art === 'anzahl' || istAnzahlfeld(schritt.feld)) && Number.isFinite(Number(schritt.wert))) {
    return anzahlwort(Number(schritt.wert))
  }

  if (schritt.art === 'zahl' && istProzentfeld(schritt.feld)) {
    return prozentangabe(schritt.wert)
  }

  if (schritt.art === 'zahl') {
    return betrag(Number(schritt.wert))
  }

  if (schritt.art === 'periode') {
    return periodenname(Number(schritt.wert))
  }

  if (schritt.art === 'konto') {
    return kontoname(schritt.wert)
  }

  return schritt.wert
}

/**
 * Die Konsolidierungsschritte eines Laufs, aufklappbar bis zur Herleitung.
 *
 * Das ist Merkmal K7 in seiner sichtbaren Form: Jede Konzernzahl lässt sich bis
 * zu ihren Ausgangswerten auflösen. Die Buchungen werden erst beim Aufklappen
 * geholt — ein Lauf über einen echten Konzern hat Zehntausende, und niemand
 * will sie alle sehen, nur die eine.
 */
export function Vorgaenge({ laufId, vorgaenge }: { laufId: number; vorgaenge: Vorgangskopf[] }) {
  return (
    <div>
      {vorgaenge.map((vorgang) => (
        <Vorgang key={vorgang.id} laufId={laufId} vorgang={vorgang} />
      ))}
    </div>
  )
}

function Vorgang({ laufId, vorgang }: { laufId: number; vorgang: Vorgangskopf }) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('vorgaenge:vorgang')

  const [buchungen, setBuchungen] = useState<Buchungszeile[] | null>(null)
  const [laedt, setLaedt] = useState(false)

  async function aufklappen(event: React.SyntheticEvent<HTMLDetailsElement>) {
    if (!event.currentTarget.open || buchungen !== null || laedt) {
      return
    }

    setLaedt(true)
    try {
      setBuchungen(await api.buchungen(laufId, vorgang.id))
    } finally {
      setLaedt(false)
    }
  }

  return (
    <details className="vorgang" onToggle={aufklappen}>
      <summary>
        <span className="nummer">{vorgang.reihenfolge}</span>
        <span className="name">{vorgangsname(vorgang)}</span>
        {/* Ohne Zahl der Buchungen und Rechenzeit in Millisekunden (7.2
            Abschnitt 36, R1143); was gebucht wurde, steht aufgeklappt. */}
      </summary>

      <div style={{ padding: '10px 12px' }}>
        {vorgang.meldungen.length > 0 && (
          <ul style={{ margin: '0 0 12px', paddingLeft: '18px' }} className="erlaeuterung">
            {vorgang.meldungen.map((meldung, i) => (
              <li key={i}>{satz(meldung)}</li>
            ))}
          </ul>
        )}

        {laedt && <p className="hinweis">{t('vorgang.laedt')}</p>}

        {buchungen !== null && buchungen.length === 0 && (
          <p className="hinweis">{t('vorgang.leer')}</p>
        )}

        {/*
          **Im Rahmen wie jede andere Tabelle** (R412). Ohne ihn klebte der Kopf
          am Fenster und schoebe sich beim Rollen unter die Kontextzeile -- bei
          drei Buchungen faellt das nicht auf, bei dreihundert schon. Der Rahmen
          gibt ihm eine Kante, an der er stehen bleibt.
        */}
        {buchungen !== null && buchungen.length > 0 && (
          <div className="tabellenrahmen">
          <table ref={spaltenrahmen} className="ziehbaretabelle">
            <Spaltenmassleiste masse={spaltenmasse} />
            <thead>
              <tr>
                <th>{t('vorgang.spalte.nummer')}</th>
                <th>{t('vorgang.spalte.einheit')}</th>
                <th>{t('vorgang.spalte.konto')}</th>
                <th className="zahl">{t('vorgang.spalte.betrag')}</th>
                <th>{t('vorgang.spalte.regel')}</th>
              </tr>
            </thead>
            <tbody>
              {buchungen.map((buchung) => (
                <tr key={buchung.id}>
                  <td className="zahl">{buchung.buchungsnummer}</td>
                  <td>{buchung.einheit ? gesellschaftsname(buchung.einheit) : ohneWert('gesellschaft')}</td>
                  <td>{kontobezeichnung(buchung.konto)}</td>
                  <td className={`zahl ${buchung.betrag < 0 ? 'minus' : ''}`}>
                    <Betrag wert={buchung.betrag} />
                  </td>
                  <td>
                    <div>{regelname(buchung.regel)}</div>
                    <div className="erlaeuterung">{satz(buchung.erlaeuterung)}</div>
                    {buchung.herleitung.length > 0 && (
                      <dl className="herleitung">
                        {buchung.herleitung.map((schritt, i) => (
                          <div key={i} style={{ display: 'contents' }}>
                            <dt>{herleitungsname(schritt.feld)}</dt>
                            <dd>{schrittwert(schritt)}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        {/* Kein Prüfcode am einzelnen Schritt (7.2 Abschnitt 37, R1147): Er
            steht, wo ein Prüfer vergleicht — im Konzernabschluss-Monitor, im Ausdruck, in der
            Prüferansicht und im Anhang. */}
      </div>
    </details>
  )
}
